import {getStorage} from "@/store/util.store";
import {normalizeStoredAccessToken} from "@/store/util.token";
import {useAuthStore} from "../../store/useAuth.store";

import {loadAbort} from "./loadAbort.utility";
import privateInstance from "./private.instance";
import {extractHttpErrorInfo, shouldLogoutForSessionError} from "./sessionAuthError";

const setHeaders = async additionalHeaders => {
  const accessToken = normalizeStoredAccessToken(getStorage("token"));
  if (accessToken) {
    return {
      authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      ...additionalHeaders,
    };
  }
  return {
    ...additionalHeaders,
  };
};

const isFormData = body => {
  return typeof FormData !== "undefined" && body instanceof FormData;
};

const getLogout = () => {
  return useAuthStore.getState().logout;
};

const manageCatchError = (err, options = {}) => {
  if (options.skipSessionLogout) {
    return;
  }

  const {status, errorMessage} = extractHttpErrorInfo(err);

  if (shouldLogoutForSessionError(err, status, errorMessage)) {
    getLogout()();
  }
};

const privateRequest = {
  // `options.signal` (optional) lets callers forward an external AbortSignal
  // (e.g. from TanStack Query's queryFn) so unmounts / query cancellations
  // actually abort the in-flight HTTP request. Falls back to a fresh
  // AbortController when no external signal is provided.
  // `options.skipSessionLogout` — BO/optional routes that may reject the user
  // token without meaning the whole session is invalid.
  get: async (url, body, additionalHeaders, transformResponse, options = {}) => {
    const controller = options.signal ? null : loadAbort();
    const config = {
      headers: await setHeaders(additionalHeaders),
      signal: options.signal || controller.signal,
      data: body,
      transformResponse,
    };
    try {
      const response = await privateInstance.get(url, config);
      const {status, data} = response;

      return {data, status};
    } catch (err) {
      manageCatchError(err, options);
      throw err;
    }
  },

  put: async (url, body, additionalHeaders, options = {}) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      const response = await privateInstance({
        method: "put",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      manageCatchError(err, options);
      throw err;
    }
  },
  patch: async (url, body, additionalHeaders, options = {}) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      const response = await privateInstance({
        method: "patch",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      manageCatchError(err, options);
      throw err;
    }
  },
  delete: async (url, body, additionalHeaders, options = {}) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      const response = await privateInstance({
        method: "delete",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      manageCatchError(err, options);
      throw err;
    }
  },
  post: async (url, body, additionalHeaders, options = {}) => {
    try {
      const config = {
        headers: await setHeaders(additionalHeaders),
      };
      if (isFormData(body)) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      const response = await privateInstance({
        method: "post",
        url,
        data: body,
        ...config,
      });
      const {data, status} = response;
      return {data, status};
    } catch (err) {
      manageCatchError(err, options);
      throw err;
    }
  },
};

export default privateRequest;
